import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/auth/api';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import {
  getCustomer,
  registerCustomerContact,
  resendCustomerInvitation,
  updateCustomer,
} from '@/features/customers/api';
import {
  fieldErrorsFrom,
  formatPhone,
  normalizePhone,
  validateCustomerForm,
} from '@/features/customers/customerForm';
import { CustomerActivityChips } from '@/features/customers/components/CustomerChips';
import {
  CustomerOperations,
  CustomerSummary,
} from '@/features/customers/components/CustomerOperations';
import { CustomerTimeline } from '@/features/customers/components/CustomerTimeline';
import { useCustomerOperations } from '@/features/customers/useCustomerOperations';
import { useCustomerTimeline } from '@/features/customers/useCustomerTimeline';
import type {
  CustomerDetail,
  CustomerEmailNotice,
  CustomerFieldErrors,
  CustomerFormValues,
} from '@/features/customers/types';
import {
  customerStatusDescriptions,
  formatHistoryTimestamp,
  formatQuotationDate,
} from '@/features/operations/labels';
import { buildWhatsAppUrl, isWhatsAppReachable } from '@/features/operations/whatsapp';
import { StatusChip } from '@/components/StatusChip';

/// What the list or the new-account dialog hand over when they open a customer.
type DetailLocationState = {
  /// The list's query string, so the way back returns to the same search.
  from?: string;
  /// Set by the new-account dialog: whether it created the account or found an existing one.
  outcome?: 'created' | 'existing';
} | null;

function formFrom(customer: CustomerDetail): CustomerFormValues {
  return { fullName: customer.fullName, email: customer.email, phone: customer.phone ?? '' };
}

function emailNoticeMessage(notice: CustomerEmailNotice, email: string): string {
  if (notice === 'INVITATION') {
    return `Guardado. La invitación anterior dejó de funcionar y le mandamos una nueva a ${email}.`;
  }

  if (notice === 'VERIFICATION') {
    return `Guardado. El mail quedó sin verificar: le mandamos a ${email} el enlace para confirmarlo.`;
  }

  return 'Guardado.';
}

/**
 * One customer (CRM-2, #229): the account data, which the employee can correct, its state, and
 * the sections CRM-3, CRM-4 and CRM-6 fill in with quotations, notes and operations.
 *
 * Correcting the account never rewrites what its quotations recorded (BR-016).
 */
export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const { token } = useAuth();
  const location = useLocation();
  const locationState = location.state as DetailLocationState;
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [form, setForm] = useState<CustomerFormValues>({ fullName: '', email: '', phone: '' });
  const [fieldErrors, setFieldErrors] = useState<CustomerFieldErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingEmail, setIsConfirmingEmail] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [contactMessage, setContactMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [isContacting, setIsContacting] = useState(false);
  const timeline = useCustomerTimeline(token, id);
  const operations = useCustomerOperations(token, id);

  useEffect(() => {
    if (!token) {
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const loaded = await getCustomer(token, id);
        setCustomer(loaded);
        setForm(formFrom(loaded));
      } catch (error) {
        setLoadError(
          error instanceof ApiError && error.status === 404
            ? 'No encontramos ese cliente. Puede que el enlace esté mal.'
            : 'No pudimos cargar el cliente. Revisá la conexión e intentá de nuevo.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token, id]);

  if (!token) {
    return null;
  }

  const backLink = (
    <Box>
      <Button
        component={RouterLink}
        to={`/admin/customers${locationState?.from ? `?${locationState.from}` : ''}`}
        color="inherit"
      >
        ← Volver a clientes
      </Button>
    </Box>
  );

  if (isLoading) {
    return (
      <Stack spacing={3}>
        {backLink}
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      </Stack>
    );
  }

  if (!customer) {
    return (
      <Stack spacing={3}>
        {backLink}
        <Alert severity="error">{loadError}</Alert>
      </Stack>
    );
  }

  const isPending = customer.status === 'PENDING';
  const emailChanged = form.email.trim().toLowerCase() !== customer.email;
  const normalizedPhone = form.phone.trim() ? normalizePhone(form.phone) : null;
  const hasChanges =
    form.fullName.trim() !== customer.fullName ||
    emailChanged ||
    (form.phone.trim() ? normalizedPhone !== customer.phone : customer.phone !== null);

  const setField = (field: keyof CustomerFormValues, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
    setSuccessMessage(null);
  };

  const save = async () => {
    setIsConfirmingEmail(false);
    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await updateCustomer(token, customer.id, {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });

      setCustomer(result.customer);
      setForm(formFrom(result.customer));
      setSuccessMessage(emailNoticeMessage(result.emailNotice, result.customer.email));
      setInvitationMessage(null);
    } catch (error) {
      const marked = fieldErrorsFrom(error);

      if (marked) {
        setFieldErrors(marked);
      } else {
        setSaveError('No pudimos guardar los cambios. Revisá la conexión e intentá de nuevo.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage(null);
    const problems = validateCustomerForm(form, { phoneRequired: !isPending });

    if (Object.keys(problems).length) {
      setFieldErrors(problems);
      return;
    }

    // A new mail sends a link and, on an active account, unverifies it: said before, not after.
    if (emailChanged) {
      setIsConfirmingEmail(true);
      return;
    }

    void save();
  };

  const resendInvitation = async () => {
    setIsResending(true);
    setInvitationMessage(null);

    try {
      const updated = await resendCustomerInvitation(token, customer.id);
      setCustomer(updated);
      setInvitationMessage({
        severity: 'success',
        text: `Le mandamos una nueva invitación a ${updated.email}. La anterior dejó de funcionar.`,
      });
    } catch {
      setInvitationMessage({
        severity: 'error',
        text: 'No pudimos reenviar la invitación. Intentá de nuevo en un momento.',
      });
    } finally {
      setIsResending(false);
    }
  };

  /// The link opens the chat on its own: a failure here only means the contact is not on file.
  const registerWhatsAppContact = async () => {
    setContactMessage(null);
    setIsContacting(true);

    try {
      timeline.appendNote(await registerCustomerContact(token, customer.id));
      setContactMessage({ severity: 'success', text: 'Contacto registrado en el historial.' });
    } catch {
      setContactMessage({
        severity: 'error',
        text: 'Se abrió el chat, pero no pudimos dejar registrado el contacto. Agregalo como nota.',
      });
    } finally {
      setIsContacting(false);
    }
  };

  const canWhatsApp = isWhatsAppReachable(customer.phone);

  return (
    <Stack spacing={3}>
      {backLink}
      <PageHeader
        eyebrow="CLIENTE"
        title={customer.fullName}
        description={`${customer.email} · cuenta creada el ${formatQuotationDate(customer.createdAt)}`}
        badge="Uso interno"
      />

      {locationState?.outcome === 'created' ? (
        <Alert severity="success">
          Cuenta creada. Le mandamos a {customer.email} la invitación para que elija su contraseña.
        </Alert>
      ) : null}
      {locationState?.outcome === 'existing' ? (
        <Alert severity="info">
          Ese mail ya tenía una cuenta, así que no creamos otra: esta es la que existe.
        </Alert>
      ) : null}

      {operations.operations ? <CustomerSummary summary={operations.operations.summary} /> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <SectionCard
            title="Datos de la cuenta"
            description="Corregirlos no cambia lo que quedó registrado en sus cotizaciones y operaciones."
          >
            <Stack component="form" spacing={2.5} noValidate onSubmit={handleSubmit}>
              {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
              {saveError ? <Alert severity="error">{saveError}</Alert> : null}
              <TextField
                required
                fullWidth
                label="Nombre"
                value={form.fullName}
                error={Boolean(fieldErrors.fullName)}
                helperText={fieldErrors.fullName}
                onChange={(event) => setField('fullName', event.target.value)}
              />
              <TextField
                required
                fullWidth
                type="email"
                label="Mail"
                value={form.email}
                error={Boolean(fieldErrors.email)}
                helperText={
                  fieldErrors.email ||
                  (isPending
                    ? 'Si lo corregís, le mandamos la invitación al mail nuevo.'
                    : 'Si lo cambiás, queda sin verificar hasta que confirme el mail nuevo.')
                }
                onChange={(event) => setField('email', event.target.value)}
              />
              <TextField
                required={!isPending}
                fullWidth
                type="tel"
                label={isPending ? 'Teléfono (opcional)' : 'Teléfono'}
                value={form.phone}
                error={Boolean(fieldErrors.phone)}
                helperText={
                  fieldErrors.phone ||
                  (form.phone.trim() && normalizedPhone
                    ? `Se guarda como ${formatPhone(normalizedPhone)}.`
                    : 'Por ejemplo 099 123 456.')
                }
                onChange={(event) => setField('phone', event.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <PrimaryButton type="submit" disabled={isSaving || !hasChanges}>
                  {isSaving ? 'Guardando...' : 'Guardar cambios'}
                </PrimaryButton>
                {hasChanges && !isSaving ? (
                  <Button
                    color="inherit"
                    onClick={() => {
                      setForm(formFrom(customer));
                      setFieldErrors({});
                    }}
                  >
                    Descartar cambios
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Estado de la cuenta">
            <Stack spacing={2}>
              <Box>
                <StatusChip domain="customerAccount" status={customer.status} />
              </Box>
              <Typography variant="body2">{customerStatusDescriptions[customer.status]}</Typography>

              {isPending ? (
                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {customer.invitation
                      ? // The time already ends in "p. m.": a separator, not a period (#229, U-4).
                        `Última invitación: ${formatHistoryTimestamp(customer.invitation.sentAt)} · vence el ${formatQuotationDate(customer.invitation.expiresAt)}`
                      : 'No tiene una invitación vigente.'}
                  </Typography>
                  {invitationMessage ? (
                    <Alert severity={invitationMessage.severity}>{invitationMessage.text}</Alert>
                  ) : null}
                  <Box>
                    <Button variant="outlined" onClick={resendInvitation} disabled={isResending}>
                      {isResending ? 'Reenviando...' : 'Reenviar invitación'}
                    </Button>
                  </Box>
                </Stack>
              ) : null}

              <Stack spacing={0.75}>
                <Typography variant="subtitle2">Actividad</Typography>
                <CustomerActivityChips activity={customer.activity} />
              </Stack>

              <Stack spacing={0.75}>
                <Box>
                  {canWhatsApp ? (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<WhatsAppIcon />}
                      href={buildWhatsAppUrl(customer.phone!, '')}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={isContacting}
                      onClick={() => void registerWhatsAppContact()}
                    >
                      {isContacting ? 'Registrando…' : 'Escribir por WhatsApp'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<WhatsAppIcon />}
                      disabled
                    >
                      Escribir por WhatsApp
                    </Button>
                  )}
                </Box>
                {canWhatsApp ? null : (
                  <Typography variant="caption" color="text.secondary">
                    {customer.phone
                      ? 'El teléfono guardado no está en formato internacional.'
                      : 'La cuenta no tiene teléfono. Cargalo en los datos para poder escribirle.'}
                  </Typography>
                )}
                {contactMessage ? (
                  <Alert severity={contactMessage.severity}>{contactMessage.text}</Alert>
                ) : null}
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        {/*
          Each section keeps its own card: CRM-3 (#108) lists the quotations; CRM-4 (#110) fills the
          notes and timeline, CRM-6 (#230) the operations.
        */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Cotizaciones">
            <Box data-section="quotations">
              {customer.quotations.length ? (
                // A narrow card: one compact row per quotation, newest first, not a wide table.
                <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {customer.quotations.map((quotation) => {
                    const phone = `${quotation.brand} ${quotation.model}${
                      quotation.storageGb === null ? '' : ` · ${quotation.storageGb}GB`
                    }`;

                    return (
                      // What it is on the left, the way in on the right: the button keeps its
                      // place however long the phone's name or the date get.
                      <Stack
                        component="li"
                        key={quotation.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {phone}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                          >
                            <StatusChip domain="quotation" status={quotation.status} />
                            {/* With the time: two quotations of one day must not look the same
                                (U-3). */}
                            <Typography variant="caption" color="text.secondary">
                              {formatHistoryTimestamp(quotation.createdAt)}
                            </Typography>
                          </Stack>
                        </Stack>
                        {/* A button rather than the text as a link: an obvious target, and each
                            one says which quotation it opens to a screen reader. */}
                        <Button
                          component={RouterLink}
                          to={`/admin/quotations/${quotation.id}`}
                          size="small"
                          variant="outlined"
                          aria-label={`Ver cotización ${phone}`}
                          sx={{ flexShrink: 0 }}
                        >
                          Ver cotización
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="info">
                  Todavía no cotizó. Las cotizaciones que haga en la web o se le carguen en el
                  mostrador van a aparecer acá.
                </Alert>
              )}
            </Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Operaciones"
            description="Lo que compró, reservó y vendió a PhoneTec. Para cambiar algo, entrá al equipo."
          >
            <CustomerOperations
              operations={operations.operations}
              isLoading={operations.isLoading}
              error={operations.error}
              onRetry={operations.reload}
            />
          </SectionCard>
        </Grid>
        {/* The full width: each line says what happened, on which phone, and has its way in. */}
        <Grid item xs={12}>
          <SectionCard
            title="Notas e historial"
            description="Las notas de este cliente y los pasos de sus cotizaciones, reservas y compras. Las notas de cada cotización o equipo se ven allí."
          >
            <CustomerTimeline
              entries={timeline.entries}
              isLoading={timeline.isLoading}
              error={timeline.error}
              onRetry={timeline.reload}
              onAdd={timeline.add}
              onEdit={timeline.edit}
            />
          </SectionCard>
        </Grid>
      </Grid>

      <Dialog open={isConfirmingEmail} onClose={() => setIsConfirmingEmail(false)}>
        <DialogTitle>¿Cambiar el mail de la cuenta?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isPending
              ? `La invitación que le mandamos a ${customer.email} deja de funcionar y le mandamos una nueva a ${form.email.trim()}.`
              : `La cuenta queda sin verificar hasta que confirme ${form.email.trim()}: mientras tanto puede cotizar, pero no reservar ni comprar. Le mandamos el enlace al mail nuevo.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setIsConfirmingEmail(false)}>
            Cancelar
          </Button>
          <PrimaryButton onClick={() => void save()}>Cambiar el mail</PrimaryButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
